import { type ReactNode, type CSSProperties, type ComponentPropsWithoutRef, type ElementRef, forwardRef } from 'react';
import * as ToggleGroup from '@radix-ui/react-toggle-group';
import { clsx } from 'clsx';
import { segmentedControlRoot, segmentedControlItem } from './segmentedcontrol.css';

type Variant = 'default' | 'pill';

export interface SegmentedControlOption {
  value: string;
  label: ReactNode;
  disabled?: boolean;
  testId?: string;
  /** Native title + accessible name — for options whose visible label is ambiguous (e.g. single-letter day abbreviations). */
  title?: string;
}

// Single-select (exclusive) control
export interface SegmentedControlProps {
  value: string;
  onChange: (value: string) => void;
  options: SegmentedControlOption[];
  variant?: Variant;
  disabled?: boolean;
  className?: string;
  style?: CSSProperties;
  'data-testid'?: string;
}

export function SegmentedControl({
  value,
  onChange,
  options,
  variant = 'default',
  disabled = false,
  className,
  style,
  'data-testid': testId,
}: SegmentedControlProps) {
  return (
    <ToggleGroup.Root
      type="single"
      value={value}
      onValueChange={(v) => { if (v) onChange(v); }}
      className={clsx(segmentedControlRoot({ variant }), className)}
      disabled={disabled}
      style={style}
      data-testid={testId}
    >
      {options.map((opt) => (
        <ToggleGroup.Item
          key={opt.value}
          value={opt.value}
          disabled={opt.disabled}
          className={segmentedControlItem({ variant })}
          data-testid={opt.testId}
          title={opt.title}
          aria-label={opt.title}
        >
          {opt.label}
        </ToggleGroup.Item>
      ))}
    </ToggleGroup.Root>
  );
}

// Multi-select control (for day-of-week pickers etc.)
export interface MultiSegmentedControlProps {
  value: string[];
  onChange: (values: string[]) => void;
  options: SegmentedControlOption[];
  variant?: Variant;
  disabled?: boolean;
  className?: string;
  style?: CSSProperties;
  'data-testid'?: string;
}

export function MultiSegmentedControl({
  value,
  onChange,
  options,
  variant = 'default',
  disabled = false,
  className,
  style,
  'data-testid': testId,
}: MultiSegmentedControlProps) {
  return (
    <ToggleGroup.Root
      type="multiple"
      value={value}
      onValueChange={onChange}
      className={clsx(segmentedControlRoot({ variant }), className)}
      disabled={disabled}
      style={style}
      data-testid={testId}
    >
      {options.map((opt) => (
        <ToggleGroup.Item
          key={opt.value}
          value={opt.value}
          disabled={opt.disabled}
          className={segmentedControlItem({ variant })}
          data-testid={opt.testId}
          title={opt.title}
          aria-label={opt.title}
        >
          {opt.label}
        </ToggleGroup.Item>
      ))}
    </ToggleGroup.Root>
  );
}

// Children-based API for complex content (icons, tooltips, conditional items)
export interface SegmentedControlRootProps {
  value: string;
  onChange: (value: string) => void;
  variant?: Variant;
  disabled?: boolean;
  className?: string;
  style?: CSSProperties;
  'data-testid'?: string;
  children: ReactNode;
}

export function SegmentedControlRoot({
  value,
  onChange,
  variant = 'default',
  disabled = false,
  className,
  style,
  'data-testid': testId,
  children,
}: SegmentedControlRootProps) {
  return (
    <ToggleGroup.Root
      type="single"
      value={value}
      onValueChange={(v) => { if (v) onChange(v); }}
      className={clsx(segmentedControlRoot({ variant }), className)}
      disabled={disabled}
      style={style}
      data-testid={testId}
    >
      {children}
    </ToggleGroup.Root>
  );
}

export interface SegmentedControlItemProps
  extends Omit<
    ComponentPropsWithoutRef<typeof ToggleGroup.Item>,
    'value' | 'disabled' | 'className' | 'style' | 'children'
  > {
  value: string;
  variant?: Variant;
  disabled?: boolean;
  className?: string;
  style?: CSSProperties;
  'data-testid'?: string;
  'aria-label'?: string;
  children: ReactNode;
}

export const SegmentedControlItem = forwardRef<
  ElementRef<typeof ToggleGroup.Item>,
  SegmentedControlItemProps
>(function SegmentedControlItem(
  {
    value,
    variant = 'default',
    disabled = false,
    className,
    style,
    'data-testid': testId,
    'aria-label': ariaLabel,
    children,
    ...rest
  },
  ref,
) {
  // Forward props a parent <Tooltip>/Radix Trigger `asChild` injects (hover/focus
  // handlers, aria-describedby, ref) onto the real <button> so the item itself is
  // the tooltip trigger (Issue 6) — but DROP the injected `data-state`
  // (open/closed): it would overwrite ToggleGroup.Item's selection `data-state`
  // (on/off) that the selected-segment styling keys on. The tooltip still opens
  // via the handlers + ref.
  const triggerProps = { ...rest };
  delete (triggerProps as { 'data-state'?: unknown })['data-state'];
  return (
    <ToggleGroup.Item
      ref={ref}
      value={value}
      disabled={disabled}
      className={clsx(segmentedControlItem({ variant }), className)}
      style={style}
      data-testid={testId}
      aria-label={ariaLabel}
      {...triggerProps}
    >
      {children}
    </ToggleGroup.Item>
  );
});
